// Per-route SEO head updates. The initial document (index.html)
// carries rich defaults — meta tags, Open Graph, structured data —
// that cover the first crawler fetch. On client-side navigation React
// Router updates the URL but not the head; this hook rewrites the
// title, description, canonical, OG, and Twitter tags so each route
// has the right card when shared.
//
//   useSeo({
//     title: "Pricing · malbox",
//     description: "Honest pricing. Open source.",
//     path: "/pricing",
//   });
//
// Pass `jsonLd` to append a route-specific structured-data block —
// e.g. the FAQPage schema on the landing's FAQ, or BreadcrumbList on
// deep pages. The hook cleans up its own injections on unmount so
// SPA nav never accumulates stale blocks.
import { useEffect } from "react";

const SITE = "https://malbox.neels.dev";
const DEFAULT_OG = `${SITE}/og.png`;
const SITE_NAME = "malbox";
const SITE_SUFFIX = "malbox"; // appended to <title> unless already present

type Seo = {
  title: string;
  description: string;
  /** The route path (e.g. "/pricing"). Combined with SITE for canonical + og:url. */
  path: string;
  /** Override the OG image for this route; defaults to /og.png. */
  image?: string;
  /** Comma-separated keywords tuned to the route. */
  keywords?: string;
  /** One or more JSON-LD @graph nodes to append. Cleaned up on unmount. */
  jsonLd?: unknown[];
  /** Set to true on routes that shouldn't be indexed (403, 404, private views). */
  noindex?: boolean;
};

function setMeta(selector: string, attr: "content" | "href", value: string) {
  const el = document.head.querySelector<HTMLMetaElement | HTMLLinkElement>(selector);
  if (el) {
    el.setAttribute(attr, value);
    return;
  }
  const [tag, attrStr] = selector.replace(/^(\w+)/, "$1\0").split("\0");
  const node = document.createElement(tag);
  // selector shape: tag[name="..."] | tag[property="..."] | tag[rel="..."]
  const m = /\[([^=]+)="([^"]+)"\]/.exec(attrStr ?? "");
  if (m) node.setAttribute(m[1], m[2]);
  node.setAttribute(attr, value);
  document.head.appendChild(node);
}

export function useSeo(seo: Seo) {
  useEffect(() => {
    const canonical = SITE + (seo.path.startsWith("/") ? seo.path : "/" + seo.path);
    const image = seo.image ?? DEFAULT_OG;
    const title = seo.title.includes(SITE_SUFFIX) ? seo.title : `${seo.title} · ${SITE_SUFFIX}`;

    const prevTitle = document.title;
    document.title = title;

    setMeta('meta[name="description"]', "content", seo.description);
    setMeta('link[rel="canonical"]', "href", canonical);
    if (seo.keywords) setMeta('meta[name="keywords"]', "content", seo.keywords);
    setMeta('meta[name="robots"]', "content", seo.noindex ? "noindex,nofollow" : "index,follow,max-image-preview:large,max-snippet:-1");

    setMeta('meta[property="og:url"]', "content", canonical);
    setMeta('meta[property="og:title"]', "content", seo.title);
    setMeta('meta[property="og:description"]', "content", seo.description);
    setMeta('meta[property="og:image"]', "content", image);
    setMeta('meta[property="og:site_name"]', "content", SITE_NAME);

    setMeta('meta[name="twitter:title"]', "content", seo.title);
    setMeta('meta[name="twitter:description"]', "content", seo.description);
    setMeta('meta[name="twitter:image"]', "content", image);
    setMeta('meta[name="twitter:card"]', "content", "summary_large_image");

    // JSON-LD: inject each block as its own <script> for easy cleanup.
    const scripts: HTMLScriptElement[] = [];
    for (const node of seo.jsonLd ?? []) {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.dataset.seoRoute = seo.path;
      s.textContent = JSON.stringify(node);
      document.head.appendChild(s);
      scripts.push(s);
    }

    return () => {
      document.title = prevTitle;
      for (const s of scripts) s.remove();
    };
  }, [seo.title, seo.description, seo.path, seo.image, seo.keywords, seo.noindex, seo.jsonLd]);
}
